import type { Session } from "@supabase/supabase-js";
import { worldwardenEntitlementSlug } from "../lib/config";
import { requireSupabase } from "../lib/supabase";

export interface WorkspaceAccess {
  workspaceId: string;
  role: string;
}

export async function hasWorldwardenAccess(userId: string): Promise<boolean> {
  const db = requireSupabase();

  const [entitlementResult, adminResult] = await Promise.all([
    db
      .from("product_entitlements")
      .select("product_slug,status")
      .eq("user_id", userId)
      .eq("product_slug", worldwardenEntitlementSlug)
      .eq("status", "active")
      .maybeSingle(),
    db.from("platform_admins").select("user_id").eq("user_id", userId).maybeSingle(),
  ]);

  if (entitlementResult.error) {
    throw new Error(
      `Unable to verify Worldwarden access: ${entitlementResult.error.message}`,
    );
  }

  if (adminResult.error) {
    throw new Error(
      `Unable to verify platform administrator access: ${adminResult.error.message}`,
    );
  }

  return Boolean(entitlementResult.data || adminResult.data);
}

export async function ensureWorkspace(session: Session): Promise<WorkspaceAccess> {
  const db = requireSupabase();
  const principalId = session.user.id;

  const { data: memberships, error: membershipError } = await db
    .from("workspace_members")
    .select("workspace_id,role,created_at")
    .eq("principal_id", principalId)
    .order("created_at", { ascending: true })
    .limit(1);

  if (membershipError) {
    throw new Error(`Unable to load your Worldwarden workspace: ${membershipError.message}`);
  }

  if (memberships?.length) {
    return {
      workspaceId: memberships[0].workspace_id,
      role: memberships[0].role,
    };
  }

  const workspaceName = session.user.email
    ? `${session.user.email}'s Worldwarden`
    : "My Worldwarden";

  const workspaceId = crypto.randomUUID();

  const { error: workspaceError } = await db.from("workspaces").insert({
    id: workspaceId,
    name: workspaceName,
    created_by: principalId,
  });

  if (workspaceError) {
    throw new Error(
      `Unable to create your Worldwarden workspace: ${workspaceError.message}`,
    );
  }

  const { error: ownerError } = await db.from("workspace_members").insert({
    workspace_id: workspaceId,
    principal_id: principalId,
    role: "owner",
  });

  if (ownerError) {
    throw new Error(`Unable to establish workspace ownership: ${ownerError.message}`);
  }

  return {
    workspaceId,
    role: "owner",
  };
}
